/**
 * Convert experiment tracking bridge.
 *
 * Detects when Convert.com activates an experience — on initial page load or
 * after a manual `_conv_q.push(['executeExperiment', id])` later in the
 * session — and emits matching events to:
 *
 *   1. dataLayer (consumed by GTM tags / GA)
 *   2. Meta Pixel via fbq('track', 'Experiment', …) with an eventID for
 *      server-side dedup against the Conversions API feed
 *   3. the persistent order-tag store (order-tags.js, localStorage) — read by
 *      plans.js and store.js, which compose every recorded exposure into the
 *      cart line items' _order_tag property. Persistence outlives the session
 *      AND the experiment being turned off in Convert; tags from a previous
 *      session that didn't re-trigger compose with an ExpR- prefix instead of
 *      Exp- (see order-tags.js).
 *
 * Replaces the prior page-view-triggered GTM Custom HTML tag, which only ran
 * once at load and missed any experiment activated later — e.g. the quiz
 * completion variant assigned inside style-profile.js.
 *
 * Cross-page persistence: an experiment started upstream (e.g. the quiz)
 * lives in Convert's `currentData` only on the trigger page, which then
 * redirects away; its Location keys on a `window` flag that the navigation
 * wipes, so it never re-executes downstream where order_tag is actually read.
 * To fix this without touching Convert's shared Global Project JS, the
 * upstream page persists `convExperiment` ({ id, variationId, flag }) and
 * rehydrate() re-asserts the flag + assignment and re-executes the experiment
 * (triggerIntegrations:false) on each later page so currentData repopulates.
 *
 * Detection strategy: poll `window.convert.currentData.experiences` once per
 * second. Two levels of dedup:
 *   - In-memory Set: ignores re-evaluations within a single page load.
 *   - sessionStorage: because the experiment re-executes on every funnel page,
 *     the dataLayer/Meta exposure is emitted at most once per session per
 *     (experiment_id, variation_id).
 * The order-tag store is intentionally refreshed on every page (idempotent
 * upsert) so the add-to-cart on the plans/store page always carries the
 * current variation, independently of whether the analytics exposure has
 * already been sent.
 *
 * window.smOrderTags is registered by assets/order-tags.js (its own Vite
 * entry, loaded before this bundle — see that file's LOADING MODEL note for
 * why this is a global instead of an import: classic-script bundles can't
 * share module chunks).
 */

const SEEN = new Set();
const POLL_INTERVAL = 1000;
const REPORTED_KEY = 'conv_exp_reported';

function uid() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getExperienceName(id) {
  const data = window.convert && window.convert.data;
  if (!data) return null;
  // Convert's config lives under `experiences` (current script) or
  // `experiments` (legacy), and may be either an array of objects or an
  // object keyed by id depending on script version. Mirror Convert's own
  // integration helper so the name resolves across all shapes; otherwise a
  // shape mismatch silently aborts trackActivation before anything fires.
  const cfg = data.experiences || data.experiments;
  if (!cfg) return null;
  const match = Array.isArray(cfg)
    ? cfg.find((e) => String(e.id) === String(id))
    : cfg[id];
  if (!match) return null;
  // Current script exposes `name`; legacy scripts used `n` / `test_name`.
  return match.name || match.n || match.test_name || null;
}

function getReported() {
  try {
    const raw = sessionStorage.getItem(REPORTED_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (_e) {
    return [];
  }
}

function markReported(key) {
  try {
    const arr = getReported();
    if (arr.indexOf(key) === -1) {
      arr.push(key);
      sessionStorage.setItem(REPORTED_KEY, JSON.stringify(arr));
    }
  } catch (_e) {
    // sessionStorage unavailable (private mode, quota); the in-memory SEEN
    // set still prevents duplicates within a page load.
  }
}

function trackActivation(id) {
  const ce = window.convert.currentData.experiences[id];
  if (!ce || !ce.variation) return;

  const variationId = ce.variation.id;
  const key = id + '-' + variationId;
  if (SEEN.has(key)) return;

  const experienceName = getExperienceName(id);
  if (!experienceName) return;

  SEEN.add(key);

  const variationName = ce.variation.name;

  // Persist the exposure (order-tags.js: localStorage upsert keyed by
  // experiment id + a sessionStorage "executed this session" marker). Must
  // happen on every activation regardless of whether the analytics exposure
  // has already been sent this session, so an add-to-cart on any funnel page
  // — or on a return visit after the test is turned off — carries the tag.
  // Experiment ID + variation NAME in the tag body: Shopify order tags max
  // out at 40 characters, and the old full-name format ("Exp-<experiment
  // name>-<variation name>") routinely exceeded that, so Flow's "Add order
  // tags" action rejected them and no order was ever tagged. The variation
  // name is kept so the analytics team can read which arm a tag refers to
  // without a Convert lookup; sanitization/length-capping (and the fresh
  // Exp- vs returning ExpR- prefix) live in order-tags.js.
  // Fallback if the order-tags.js script tag ever goes missing: compute the
  // tag locally and park it in the legacy sessionStorage slot, which
  // plans.js/store.js still read as a composition extra — session-scoped
  // tagging keeps working, only cross-session persistence is lost.
  let tagName;
  if (window.smOrderTags && typeof window.smOrderTags.recordConvertTag === 'function') {
    tagName = window.smOrderTags.recordConvertTag(id, variationName);
  } else {
    tagName = ('Exp-' + id + '-' + variationName)
      .replace(/[^a-zA-Z0-9 -]/g, '')
      .slice(0, 40);
    try {
      sessionStorage.setItem('order_tag', tagName);
    } catch (_e) {
      // sessionStorage unavailable (private mode, quota); non-fatal.
    }
  }
  if (!tagName) return;

  // Analytics exposure is sent once per session per experiment|variation.
  // Because the experiment re-executes across the funnel, firing on every
  // page would over-count exposures in GA/Meta. The order-tag store (above)
  // is still refreshed every page regardless.
  const alreadyReported = getReported().indexOf(key) !== -1;

  // Logged on every activation (once per page load, guarded by SEEN) so the
  // signal is visible even after the exposure has been deduped for the session.
  console.log(
    '🧪 Activating test: ' + experienceName,
    '| Variation: ' + variationName,
    '| Tag: ' + tagName,
    alreadyReported ? '| order-tag store refreshed (exposure already sent this session)' : '| firing exposure'
  );

  if (alreadyReported) return;
  markReported(key);

  const eventId = uid();

  // `experence_id` [sic] — typo preserved for parity with the prior GTM
  // tag and any downstream consumers already mapped to that key.
  const payload = {
    experence_id: id,
    variation_id: variationId,
    experience_name: experienceName,
    variation_name: variationName,
    tag: tagName,
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(Object.assign({ event: 'Experiment', event_id: eventId }, payload));

  const fireFb = () => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Experiment', payload, { eventID: eventId });
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fireFb, { once: true });
  } else {
    fireFb();
  }
}

function scan() {
  const c = window.convert;
  if (!c || !c.currentData || !c.currentData.experiences) return;
  const experiences = c.currentData.experiences;
  for (const id in experiences) {
    if (Object.prototype.hasOwnProperty.call(experiences, id)) {
      trackActivation(id);
    }
  }
}

// Re-trigger an experiment that was started upstream (e.g. the quiz) so its
// data is "current" again on this page. Convert's per-page `currentData` —
// which order_tag and the exposure event are derived from — is wiped by the
// navigation away from the trigger page, and the experiment's Location keys
// on a `window` flag that doesn't survive navigation. The upstream page
// persists `convExperiment` ({ id, variationId, flag }); here we re-assert the
// flag + assignment and re-execute with triggerIntegrations:false (data only,
// no duplicate Convert-native integration fires) so currentData repopulates.
function rehydrate() {
  let info;
  try {
    const raw = sessionStorage.getItem('convExperiment');
    if (!raw) return;
    info = JSON.parse(raw);
  } catch (_e) {
    return;
  }
  if (!info || !info.id) return;

  if (info.flag) {
    window[info.flag] = true;
  }

  window._conv_q = window._conv_q || [];
  if (info.variationId) {
    window._conv_q.push(['assignVariation', info.id, info.variationId]);
  }
  window._conv_q.push({
    what: 'executeExperiment',
    params: { experienceId: String(info.id), triggerIntegrations: false },
  });
}

function start() {
  rehydrate();
  scan();
  setInterval(scan, POLL_INTERVAL);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
